import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

export type SpeechRecognitionErrorCode =
  | 'not-allowed'
  | 'no-speech'
  | 'audio-capture'
  | 'network'
  | 'aborted'
  | 'service-not-allowed'
  | 'bad-grammar'
  | 'language-not-supported'
  | 'unknown';

export interface SpeechState {
  isRecording: boolean;
  finalTranscript: string;
  interimTranscript: string;
  error: string | null;
  errorCode: SpeechRecognitionErrorCode | null;
}

export interface StartRecordingOptions {
  /** When true, clears transcript buffers and invalidates stale handler events. */
  freshUtterance?: boolean;
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  }
}

@Injectable({
  providedIn: 'root',
})
export class SpeechToTextService implements OnDestroy {
  /** Web Speech API (vendor-prefixed); typed loosely for TS DOM variance. */
  private recognition: SpeechRecognition | null = null;
  private languageCode = 'en-US';
  private continuousMode = true;
  private shouldRestartAfterNoSpeech = false;
  private destroyed = false;

  /** Invalidates in-flight onresult/onend from prior capture cycles. */
  private utteranceGeneration = 0;

  private state = new BehaviorSubject<SpeechState>({
    isRecording: false,
    finalTranscript: '',
    interimTranscript: '',
    error: null,
    errorCode: null,
  });

  private isInitialized = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initializeSpeechRecognition();
    }
  }

  /** True when Web Speech API recognition is available (typically Chromium). */
  isSupported(): boolean {
    if (typeof window === 'undefined') return false;
    return !!(
      window.SpeechRecognition ||
      (window as Window & { webkitSpeechRecognition?: typeof SpeechRecognition })
        .webkitSpeechRecognition
    );
  }

  /**
   * Starts a fresh capture cycle: bumps generation and clears transcript buffers.
   * Stale async onresult/onend events from prior cycles are ignored.
   */
  beginNewUtterance(): void {
    this.utteranceGeneration += 1;
    this.updateState({
      finalTranscript: '',
      interimTranscript: '',
    });
    this.devLog('transcript reset', { generation: this.utteranceGeneration });
  }

  /**
   * Starts recognition once to surface permission prompt; stops immediately on first result or error.
   */
  requestPermission(): Promise<boolean> {
    return new Promise(resolve => {
      if (!this.isSupported() || !this.recognition) {
        resolve(false);
        return;
      }
      const r = this.recognition;
      const onStart = () => {
        cleanup();
        try {
          r.stop();
        } catch {
          /* ignore */
        }
        resolve(true);
      };
      const onError = () => {
        cleanup();
        resolve(false);
      };
      const cleanup = () => {
        r.removeEventListener('start', onStart);
        r.removeEventListener('error', onError);
      };
      r.addEventListener('start', onStart);
      r.addEventListener('error', onError);
      try {
        r.start();
      } catch {
        cleanup();
        resolve(false);
      }
    });
  }

  private initializeSpeechRecognition(): void {
    if (this.destroyed || typeof window === 'undefined') return;
    try {
      const Ctor =
        window.SpeechRecognition ||
        (window as Window & { webkitSpeechRecognition?: typeof SpeechRecognition })
          .webkitSpeechRecognition;
      if (!Ctor) {
        this.updateState({
          error: 'Speech recognition not supported in this browser',
          errorCode: 'unknown',
        });
        return;
      }
      this.recognition = new Ctor();
      this.setupRecognitionConfig();
      this.setupRecognitionHandlers();
      this.isInitialized = true;
    } catch (e) {
      this.updateState({
        error: 'Speech recognition not supported in this browser',
        errorCode: 'unknown',
      });
      console.error('Speech recognition not supported:', e);
    }
  }

  private setupRecognitionConfig(): void {
    if (!this.recognition) return;
    this.recognition.continuous = this.continuousMode;
    this.recognition.interimResults = true;
    this.recognition.lang = this.languageCode;
  }

  private mapError(raw: string): {
    message: string;
    code: SpeechRecognitionErrorCode;
  } {
    const code = (raw || 'unknown') as SpeechRecognitionErrorCode;
    const messages: Record<string, string> = {
      'not-allowed': 'Microphone permission denied.',
      'no-speech': 'No speech detected.',
      'audio-capture': 'No microphone found.',
      network: 'Network error during recognition.',
      aborted: 'Recognition aborted.',
      'service-not-allowed': 'Speech service not allowed.',
      'bad-grammar': 'Recognition grammar error.',
      'language-not-supported': 'Language not supported.',
    };
    return {
      code: messages[raw] ? (raw as SpeechRecognitionErrorCode) : 'unknown',
      message: messages[raw] || `Speech recognition error: ${raw}`,
    };
  }

  private setupRecognitionHandlers(): void {
    if (!this.recognition) return;
    const r = this.recognition;

    r.onstart = () => {
      this.shouldRestartAfterNoSpeech = this.continuousMode;
      this.updateState({
        isRecording: true,
        error: null,
        errorCode: null,
      });
      this.devLog('recognition start', { generation: this.utteranceGeneration });
    };

    r.onend = () => {
      const generationAtEnd = this.utteranceGeneration;
      const merged = this.getCombinedTranscript();
      if (generationAtEnd === this.utteranceGeneration) {
        this.updateState({
          isRecording: false,
          finalTranscript: merged,
          interimTranscript: '',
        });
        this.devLog('recognition end', {
          generation: generationAtEnd,
          finalLength: merged.length,
        });
      }
      if (
        this.destroyed ||
        !this.shouldRestartAfterNoSpeech ||
        !this.continuousMode
      ) {
        return;
      }
      const last = this.state.value.errorCode;
      if (last === 'no-speech' && this.recognition) {
        setTimeout(() => {
          if (this.destroyed || !this.recognition) return;
          if (generationAtEnd !== this.utteranceGeneration) return;
          try {
            this.recognition.start();
          } catch {
            /* already started */
          }
        }, 150);
      }
    };

    r.onresult = (event: SpeechRecognitionEvent) => {
      const generationAtResult = this.utteranceGeneration;
      let interim = '';
      let finalChunk = this.state.value.finalTranscript;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const piece = res[0]?.transcript ?? '';
        if (res.isFinal) {
          finalChunk = `${finalChunk} ${piece}`.trim();
          this.devLog('final transcript generated', {
            generation: generationAtResult,
            piece: piece.slice(0, 120),
          });
        } else {
          interim += piece;
        }
      }

      if (generationAtResult !== this.utteranceGeneration) {
        return;
      }

      this.updateState({
        finalTranscript: finalChunk,
        interimTranscript: interim.trim(),
      });
    };

    r.onerror = (event: SpeechRecognitionErrorEvent) => {
      const mapped = this.mapError(event.error);
      this.updateState({
        isRecording: false,
        error: mapped.message,
        errorCode: mapped.code,
      });
    };
  }

  private updateState(newState: Partial<SpeechState>): void {
    this.state.next({
      ...this.state.value,
      ...newState,
    });
  }

  private devLog(event: string, data?: Record<string, unknown>): void {
    if (environment.production) return;
    console.info(`[voice-stt] ${event}`, data ?? '');
  }

  setContinuous(continuous: boolean): void {
    this.continuousMode = continuous;
    if (this.recognition) {
      this.recognition.continuous = continuous;
    }
  }

  startRecording(opts?: StartRecordingOptions): void {
    if (this.destroyed) return;
    if (opts?.freshUtterance) {
      this.beginNewUtterance();
    }
    if (!this.isInitialized) {
      this.initializeSpeechRecognition();
    }
    if (!this.recognition) {
      this.updateState({
        error: 'Speech recognition not available',
        errorCode: 'unknown',
      });
      return;
    }
    this.shouldRestartAfterNoSpeech = true;
    try {
      this.recognition.start();
    } catch {
      try {
        this.recognition.stop();
        setTimeout(() => {
          if (!this.destroyed && this.recognition) {
            try {
              this.recognition.start();
            } catch {
              /* ignore */
            }
          }
        }, 120);
      } catch {
        /* ignore */
      }
    }
  }

  stopRecording(): void {
    this.shouldRestartAfterNoSpeech = false;
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch {
        /* ignore */
      }
    }
  }

  abort(): void {
    this.shouldRestartAfterNoSpeech = false;
    if (this.recognition) {
      try {
        this.recognition.abort();
      } catch {
        try {
          this.recognition.stop();
        } catch {
          /* ignore */
        }
      }
    }
  }

  clearTranscript(): void {
    this.utteranceGeneration += 1;
    this.updateState({
      finalTranscript: '',
      interimTranscript: '',
    });
    this.devLog('transcript cleared', { generation: this.utteranceGeneration });
  }

  clearError(): void {
    const s = this.state.value;
    if (!s.error && !s.errorCode) return;
    this.updateState({ error: null, errorCode: null });
  }

  setLanguage(languageCode: string): void {
    this.languageCode = languageCode;
    if (this.recognition) {
      this.recognition.lang = languageCode;
    }
  }

  getSnapshot(): SpeechState {
    return this.state.value;
  }

  /** Final + interim text (matches live transcript display). */
  getCombinedTranscript(snapshot?: SpeechState): string {
    const s = snapshot ?? this.state.value;
    return [s.finalTranscript, s.interimTranscript].filter(Boolean).join(' ').trim();
  }

  /** Merge interim into final before submit or when recognition ends. */
  commitInterimToFinal(): string {
    const merged = this.getCombinedTranscript();
    this.updateState({
      finalTranscript: merged,
      interimTranscript: '',
    });
    this.devLog('final transcript committed', {
      generation: this.utteranceGeneration,
      length: merged.length,
    });
    return merged;
  }

  getSpeechState(): Observable<SpeechState> {
    return this.state.asObservable();
  }

  getTranscript(): Observable<string> {
    return new Observable(subscriber => {
      const sub: Subscription = this.state.subscribe(state => {
        subscriber.next(state.finalTranscript);
      });
      return () => sub.unsubscribe();
    });
  }

  getInterimTranscript(): Observable<string> {
    return new Observable(subscriber => {
      const sub: Subscription = this.state.subscribe(state => {
        subscriber.next(state.interimTranscript);
      });
      return () => sub.unsubscribe();
    });
  }

  getRecordingState(): Observable<boolean> {
    return new Observable(subscriber => {
      const sub: Subscription = this.state.subscribe(state => {
        subscriber.next(state.isRecording);
      });
      return () => sub.unsubscribe();
    });
  }

  getError(): Observable<string | null> {
    return new Observable(subscriber => {
      const sub: Subscription = this.state.subscribe(state => {
        subscriber.next(state.error);
      });
      return () => sub.unsubscribe();
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.shouldRestartAfterNoSpeech = false;
    this.abort();
    this.recognition = null;
    this.isInitialized = false;
  }

  ngOnDestroy(): void {
    this.destroy();
  }
}
